/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  APIReportResponseInterface,
  ReportsInitialProperties,
  ReportsInterFace,
  ReportFilterInterFace,
  RequestReportInterFace,
} from '../../interface/report-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import reportApi from '../../utils/api/reports';

import { setNotificationMessage } from './NotificationManagementSlice';

// a thunk to fetch users
export const fetchAllReports = createAsyncThunk<APIReportResponseInterface, RequestReportInterFace>(
  'reports-management/fetchAllReports',
  async (data: RequestReportInterFace, { dispatch }) => {
    const response = await reportApi
      .fetchReports(removeEmptyKeys(data))
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return {
      ...response.data,
      results: response.data.results.map((consumerData: ReportsInterFace) => ({
        ...consumerData,
        totalPowerConsumed: parseInt(consumerData?.totalPowerConsumed, 10) / 1000,
        totalRevenue: parseFloat(consumerData?.totalRevenue).toFixed(2),
        transactionStartsAt: ConvertUTCtoDate(consumerData?.transactionStartsAt),
        id: consumerData._id,
      })),
    };
  },
);

// a thunk to fetch devices filters
export const fetchReportManagementFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('account-management/fetchAccountManagementFilters', async (_, { dispatch }) => {
  try {
    const response = await reportApi.fetchReportListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// Apply default filters
const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);

/**
 * Filter date format func
 * @param {any} date - The date string.
 * @returns {any} - The returned value.
 */
const formatDate = (date: Date): string => date.toISOString().split('T')[0];

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
  {
    name: 'Date Range',
    key: 'startDate_endDate',
    value: '',
    key1: 'startDate',
    value1: formatDate(yesterday),
    key2: 'endDate',
    value2: formatDate(today),
    type: 'dateRange',
    listOfValue: [],
    applyFilter: true,
    enable: true,
    disableClearFilter: true,
    disableClearFilterMessage: 'Date filter is required!',
    FEAddedFilter: true, // This is used to identify if the filter is added from FE
  },
];

// Initial state
const initialState: ReportsInitialProperties = {
  reports: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
};

// Create the slice
export const reportsSlice = createSlice({
  name: 'report-management',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateReportManagementFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state.
     */
    disableReportManagementFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Set Account filter data function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setReportFilterData: (
      state: ReportsInitialProperties,
      action: PayloadAction<ReportFilterInterFace>,
    ) => {
      state.filter = action.payload;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder: ActionReducerMapBuilder<ReportsInitialProperties>) => {
    builder
      .addCase(fetchAllReports.pending, (state: ReportsInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllReports.fulfilled,
        (state: ReportsInitialProperties, action: PayloadAction<APIReportResponseInterface>) => {
          state.loading = false;
          state.reports = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllReports.rejected, (state: ReportsInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchReportManagementFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchReportManagementFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload].filter(
            (object, index, self) => index === self.findIndex((t) => t.key === object.key),
          );
        },
      )
      .addCase(fetchReportManagementFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  setReportFilterData,
  updateReportManagementFilters,
  disableReportManagementFilterApplied,
} = reportsSlice.actions;
// Export the reducer
export default reportsSlice.reducer;
